import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import "./Items.css";

type ParamType = "number" | "text" | "select" | "boolean" | "date";
interface Column { id: string; label: string; unit: string | null; dataType: ParamType }
interface ReportRow {
  id: string;
  code: string;
  orderCode: string;
  itemCode: string;
  itemName: string;
  qty: number;
  uom: string;
  locationName: string;
  locationCode: string;
  lotCode: string | null;
  producedAt: string;
  values: Record<string, string>;
}

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}
function fmtDateTime(d: string): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(d));
}
function cellValue(c: Column, raw: string | undefined): string {
  if (raw == null || raw === "") return "";
  if (c.dataType === "boolean") return raw === "true" ? "Evet" : "Hayır";
  return raw;
}

/** Parti parametre raporu — merkez + tarih aralığı seç, parametreleri tablo + Excel olarak gör. */
export function ProductionParamReportPage() {
  const [searchParams] = useSearchParams();
  const centersQ = trpc.location.list.useQuery(undefined, { staleTime: 60_000 });
  const centers = useMemo(
    () => ((centersQ.data ?? []) as Array<{ id: string; code: string; name: string; type: string; isActive: boolean }>)
      .filter((l) => l.type === "uretim_merkezi"),
    [centersQ.data],
  );

  const [centerId, setCenterId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  // İlk yüklemede ?merkez= varsa veya tek merkez varsa otomatik seç.
  useEffect(() => {
    if (centerId || centers.length === 0) return;
    const fromUrl = searchParams.get("merkez");
    if (fromUrl && centers.some((c) => c.id === fromUrl)) setCenterId(fromUrl);
    else if (centers.length === 1) setCenterId(centers[0]!.id);
  }, [centers, centerId, searchParams]);

  const reportQ = trpc.productionBatch.paramReport.useQuery(
    { centerId, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined },
    { enabled: !!centerId, staleTime: 10_000 },
  );

  const columns = (reportQ.data?.columns ?? []) as Column[];
  const rows = (reportQ.data?.rows ?? []) as ReportRow[];
  const visibleCols = columns.filter((c) => !hidden.has(c.id));
  const centerName = reportQ.data?.centerName ?? centers.find((c) => c.id === centerId)?.name ?? "";

  function toggleCol(id: string) {
    setHidden((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function exportExcel() {
    const header = (c: Column) => (c.unit ? `${c.label} (${c.unit})` : c.label);
    const data = rows.map((r) => {
      const o: Record<string, string | number> = {
        "Parti Kodu": r.code,
        "Sipariş": r.orderCode,
        "Ürün Kodu": r.itemCode,
        "Ürün": r.itemName,
        "Miktar": r.qty,
        "Birim": r.uom,
        "Üretim Hattı": `${r.locationName} (${r.locationCode})`,
        "Lot": r.lotCode ?? "",
        "Tarih": fmtDateTime(r.producedAt),
      };
      for (const c of visibleCols) o[header(c)] = cellValue(c, r.values[c.id]);
      return o;
    });
    const headers = [
      "Parti Kodu", "Sipariş", "Ürün Kodu", "Ürün", "Miktar", "Birim", "Üretim Hattı", "Lot", "Tarih",
      ...visibleCols.map(header),
    ];
    const ws = XLSX.utils.json_to_sheet(data, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parti Parametreleri");
    const safe = centerName.replace(/[^\p{L}\p{N}]+/gu, "_").slice(0, 40) || "merkez";
    XLSX.writeFile(wb, `STEPERP_parti_parametre_${safe}.xlsx`);
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <Link to="/production" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Üretim</Link>
        <span className="sep">/</span>
        <span className="cur">Parametre Raporu</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Parti Parametre Raporu</h1>
          <p className="sub">Üretim merkezi parti parametrelerinin geçmişe dönük dökümü · Excel'e aktar</p>
        </div>
        <button type="button" className="btn-md primary" onClick={exportExcel} disabled={!centerId || rows.length === 0}>
          <Icon name="download" size={14} />Excel'e Aktar
        </button>
      </div>

      {/* Filtreler */}
      <div className="d-card" style={{ marginBottom: 16, padding: "14px 16px", display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div className="form-field" style={{ margin: 0, minWidth: 240 }}>
          <label className="lbl" htmlFor="pr-center">Üretim Merkezi <span className="req">*</span></label>
          <select id="pr-center" className="form-select" value={centerId} onChange={(e) => { setCenterId(e.target.value); setHidden(new Set()); }}>
            <option value="">— Merkez seç —</option>
            {centers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.isActive ? "" : " (pasif)"}</option>)}
          </select>
        </div>
        <div className="form-field" style={{ margin: 0 }}>
          <label className="lbl" htmlFor="pr-from">Başlangıç</label>
          <input id="pr-from" className="form-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} max={dateTo || undefined} />
        </div>
        <div className="form-field" style={{ margin: 0 }}>
          <label className="lbl" htmlFor="pr-to">Bitiş</label>
          <input id="pr-to" className="form-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom || undefined} />
        </div>
        {(dateFrom || dateTo) ? (
          <button type="button" className="btn-md ghost" onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ marginBottom: 1 }}>
            Tarihi temizle
          </button>
        ) : null}
      </div>

      {/* Parametre kolon seçimi */}
      {centerId && columns.length > 0 ? (
        <div style={{ marginBottom: 14, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--neutral-500)", fontWeight: 600 }}>Kolonlar:</span>
          {columns.map((c) => {
            const on = !hidden.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCol(c.id)}
                className={`badge-pill ${on ? "b-info" : ""}`}
                style={{ cursor: "pointer", border: on ? "1px solid var(--info)" : "1px solid var(--neutral-300)", background: on ? undefined : "var(--neutral-50)", color: on ? undefined : "var(--neutral-500)" }}
                aria-pressed={on}
              >
                {on ? <Icon name="check" size={11} /> : null}{c.label}{c.unit ? ` (${c.unit})` : ""}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Sonuç tablosu */}
      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <b style={{ fontSize: 13 }}>Sonuçlar{centerName ? ` — ${centerName}` : ""}</b>
          <span className="sec" style={{ color: "var(--neutral-500)", fontSize: 12 }}>{rows.length} parti</span>
          <span className="grow" />
        </div>
        <div className="dt2-scroll">
          {!centerId ? (
            <div className="empty-state"><p className="ttl">Üretim merkezi seçin</p><p className="sub">Rapor için yukarıdan bir üretim merkezi seçin.</p></div>
          ) : reportQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : reportQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{reportQ.error?.message}</p></div>
          ) : columns.length === 0 ? (
            <div className="empty-state"><p className="ttl">Bu merkezde parametre tanımı yok</p><p className="sub">Lokasyonlar sayfasında merkezin "Parametreler" butonundan tanımlayın.</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state"><p className="ttl">Kayıt bulunamadı</p><p className="sub">Seçili tarih aralığında parti üretimi yok.</p></div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Parti Kodu</th>
                  <th style={{ width: 120 }}>Sipariş</th>
                  <th>Ürün</th>
                  <th className="num" style={{ width: 110 }}>Miktar</th>
                  <th style={{ width: 140 }}>Üretim Hattı</th>
                  {visibleCols.map((c) => (
                    <th key={c.id} className={c.dataType === "number" ? "num" : undefined} style={{ width: 100 }} title={c.unit ? `${c.label} (${c.unit})` : c.label}>
                      {c.label}{c.unit ? <span style={{ color: "var(--neutral-400)", fontWeight: 400 }}> {c.unit}</span> : null}
                    </th>
                  ))}
                  <th style={{ width: 140 }}>Tarih</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><span className="cell-id">{r.code}</span></td>
                    <td><span className="mono" style={{ fontSize: 11.5 }}>{r.orderCode}</span></td>
                    <td>{r.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{r.itemCode}</span></td>
                    <td className="cell-num">{fmtQty(r.qty)} <span style={{ color: "var(--neutral-400)" }}>{r.uom}</span></td>
                    <td><span style={{ fontSize: 12 }}>{r.locationName}</span><span className="sec mono" style={{ fontSize: 10.5 }}>{r.locationCode}</span></td>
                    {visibleCols.map((c) => {
                      const v = cellValue(c, r.values[c.id]);
                      return (
                        <td key={c.id} className={c.dataType === "number" ? "cell-num" : undefined}>
                          {v !== "" ? <span className={c.dataType === "number" ? "mono" : undefined} style={{ fontSize: 12 }}>{v}</span> : <span style={{ color: "var(--neutral-300)" }}>—</span>}
                        </td>
                      );
                    })}
                    <td><span className="mono" style={{ fontSize: 11, color: "var(--neutral-600)" }}>{fmtDateTime(r.producedAt)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
